/**
 * Canonical task category taxonomy (BOARD-NEW-TASK-CATEGORY-TAXONOMY-1).
 *
 * Single source of truth in this repo for the `category` field on the
 * Task model. Mirrors the doctrine in
 * ~/Dev/context-engine-studio/docs/coordination/TASK-CATEGORY-TAXONOMY.md
 * (ratified 2026-04-07).
 *
 * Rule 4 in that doctrine is the load-bearing one for this UI: agents may
 * NOT invent new categories. Until a new category is ratified by the
 * Director and added to that doc, the New Task form must only allow
 * selection from this list. Drift here is what we are paying down.
 *
 * Historical tasks that predate 2026-04-07 may carry categories that are
 * NOT in this list. That is expected — they keep their legacy strings.
 * Only NEW tasks are constrained.
 */

export interface CategoryRoom {
  key: string;
  label: string;
  color: string;
  categories: { value: string; description: string }[];
}

export const TASK_CATEGORY_ROOMS: CategoryRoom[] = [
  {
    key: 'sakme',
    label: 'SAKME',
    color: '#a78bfa',
    categories: [
      { value: 'sakme/onboarding', description: 'First-run flow, persona setup, activation' },
      { value: 'sakme/workbench', description: 'Makers Workbench, tier drawers, skins' },
      { value: 'sakme/command-center', description: 'Director cockpit (non-Tasks tabs)' },
      { value: 'sakme/task-board', description: 'Director Command Center Tasks tab' },
      { value: 'sakme/finance', description: 'Billing, credit loop, Stripe, subscriptions' },
      { value: 'sakme/llm-routing', description: 'LLM router, BYOK, model picker' },
      { value: 'sakme/skills-ui', description: 'Skill picker, execution UI inside Sakme' },
      { value: 'sakme/bazaar', description: 'Marketplace, listings, talent discovery' },
      { value: 'sakme/stage-surfaces', description: 'Wall, Brain, Wallet, Gallery, Stand' },
      { value: 'sakme/notifications', description: 'Push, HITL approval, notification routing' },
    ],
  },
  {
    key: 'seitiate',
    label: 'SEITIATE',
    color: '#34d399',
    categories: [
      { value: 'seitiate/cli', description: 'seitiate CLI commands, CDX DX work' },
      { value: 'seitiate/docs', description: 'Developer-facing documentation' },
      { value: 'seitiate/thin-client', description: 'Tauri desktop app pages and surfaces' },
      { value: 'seitiate/dispatch-channels', description: 'Telegram, Slack, Discord adapters' },
      { value: 'seitiate/devloop-tracking', description: 'Session memory, doctor, event log' },
      { value: 'seitiate/harness', description: '12 harness primitives' },
      { value: 'seitiate/codex', description: 'Codex memory hierarchy, crystallization' },
      { value: 'seitiate/plus', description: 'Seitiate+ managed infra tier' },
      { value: 'seitiate/mcp', description: 'MCP servers and inbound MCP integrations' },
    ],
  },
  {
    key: 'closedclaw',
    label: 'CLOSEDCLAW',
    color: '#f87171',
    categories: [
      { value: 'closedclaw/certification', description: 'ACV pipeline, gauntlet, attestations' },
      { value: 'closedclaw/sandboxing', description: 'SkillSandbox, permission, isolation' },
      { value: 'closedclaw/governor-enforcement', description: 'GovernorEnforcer, ORA loop' },
      { value: 'closedclaw/safety', description: 'Threat detection, post-mortem, audit' },
      { value: 'closedclaw/identity', description: 'Provenance, attestation, bond_cert' },
      { value: 'closedclaw/enterprise', description: 'Multi-tenant governance, SLA, compliance' },
    ],
  },
  {
    key: 'skills',
    label: 'SKILLS',
    color: '#fbbf24',
    categories: [
      { value: 'skills/task-manager', description: 'Task Management Skill (DOGFOOD-OPS-1)' },
      { value: 'skills/knowledge-garden', description: 'Knowledge Garden Harvester' },
      { value: 'skills/crystallizer', description: 'Session crystallization preservation' },
      { value: 'skills/market-sentinel', description: 'Market Sentinel skill' },
      { value: 'skills/governor-canon', description: 'Canon evaluator, ORA at skill layer' },
      { value: 'skills/pipeline-connectors', description: '23 source connectors' },
      { value: 'skills/skill-authoring', description: 'Meta-work of building new skills' },
    ],
  },
  {
    key: 'launch',
    label: 'LAUNCH',
    color: '#22d3ee',
    categories: [
      { value: 'launch/v1', description: 'v1 launch-gating work' },
      { value: 'launch/v2', description: 'v2-scoped work (post-v1)' },
      { value: 'launch/strategy', description: 'Pitch, positioning, outreach, runbook' },
    ],
  },
  {
    key: 'cross',
    label: 'CROSS-PRODUCT',
    color: '#94a3b8',
    categories: [
      { value: 'director-personal', description: 'Corporate admin only Director can do' },
      { value: 'strategy', description: 'Sovereign memos, brainstorm rulings' },
      { value: 'ip', description: 'Patents, IP counsel, provisional filings' },
      { value: 'research', description: 'Papers, prior art, academic output' },
      { value: 'security', description: 'Credential rotation, key mgmt, vault' },
      { value: 'trust', description: 'Cross-product trust governance' },
      { value: 'deploy-regression', description: 'Bugs in deployed surfaces' },
      { value: 'ux-hygiene', description: 'Cross-cutting UI/UX QoL improvements' },
      { value: 'coordination', description: 'Fleet coordination, dispatch, agent registry' },
    ],
  },
];

/** Flat list of every canonical category value. */
export const CANONICAL_CATEGORIES: string[] = TASK_CATEGORY_ROOMS.flatMap((r) =>
  r.categories.map((c) => c.value),
);

const CANONICAL_SET = new Set(CANONICAL_CATEGORIES);

/** True if `value` is in the ratified taxonomy. */
export function isCanonicalCategory(value: string): boolean {
  return CANONICAL_SET.has(value);
}

/**
 * Default category for a new task on the board UI itself.
 *
 * The board IS the Director Command Center Tasks tab, so when an agent
 * creates a task from the board, the most likely home is `sakme/task-board`.
 * Callers can override via `defaultCategory`.
 */
export const DEFAULT_NEW_TASK_CATEGORY = 'sakme/task-board';
